import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Heading, Skeleton } from '@chakra-ui/react';
import type { MentionHeadlineMetric, MentionTimelinePoint } from '@fluenceur/types';
import { useMemo } from 'react';
import { MentionEmptyCard } from './MentionEmptyCard';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';

interface MentionPerformanceTimelineProps {
  points?: MentionTimelinePoint[];
  /**
   * Metric the curve plots, mirroring the API headline: `views` for TikTok,
   * `interactions` for Instagram. Defaults to interactions so callers without a
   * headline keep the Instagram behaviour.
   */
  headlineMetric?: MentionHeadlineMetric;
  isLoading?: boolean;
  isError?: boolean;
  /**
   * Boost launch timestamp. When set and inside the plotted range, a vertical
   * reference line marks "from here, we boosted" on the curve. Null or
   * out-of-range renders no marker (graceful).
   */
  launchedAt?: string | null;
}

const MINUTE_IN_MS = 60_000;

const formatElapsedMinutes = (minutes: number) => {
  if (minutes < 60) return `${Math.max(0, Math.round(minutes))}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
};

/**
 * Project a launch timestamp onto the chart's elapsed-minutes axis, using the
 * same `elapsedMinutes + 1` offset the plotted points use (the +1 keeps the log
 * scale defined at t=0). Returns null when the timestamp is missing,
 * unparseable, or falls outside the plotted point range, so an out-of-range
 * marker is dropped rather than clamped onto an edge.
 */
function resolveLaunchAxisValue(
  launchedAt: string | null,
  points: MentionTimelinePoint[]
): number | null {
  if (!launchedAt || points.length < 2) {
    return null;
  }
  const launchTime = new Date(launchedAt).getTime();
  const firstTime = new Date(points[0].capturedAt).getTime();
  const lastTime = new Date(points[points.length - 1].capturedAt).getTime();
  if (Number.isNaN(launchTime) || Number.isNaN(firstTime) || Number.isNaN(lastTime)) {
    return null;
  }
  // Outside the plotted window: render no marker.
  if (launchTime < firstTime || launchTime > lastTime) {
    return null;
  }
  const elapsedMinutes = Math.max(0, Math.round((launchTime - firstTime) / MINUTE_IN_MS));
  return elapsedMinutes + 1;
}

function TimelineChartSkeleton() {
  return (
    <Box
      h="152px"
      position="relative"
      overflow="hidden"
      bg="gray.50"
      rounded="md"
      data-testid="mention-timeline-loading"
    >
      <Box aria-hidden="true" position="absolute" inset={0} color="gray.200" opacity={0.75}>
        <svg width="100%" height="100%" viewBox="0 0 640 180" preserveAspectRatio="none">
          <g stroke="var(--chakra-colors-gray-100)" strokeWidth="1">
            <path d="M0 42H640" />
            <path d="M0 86H640" />
            <path d="M0 130H640" />
          </g>
          <path
            d="M0 128 C92 118 126 96 190 104 C268 114 302 70 370 78 C454 88 506 58 640 38"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M0 128 C92 118 126 96 190 104 C268 114 302 70 370 78 C454 88 506 58 640 38 L640 180 L0 180 Z"
            fill="var(--chakra-colors-gray-100)"
            opacity="0.7"
          />
        </svg>
      </Box>
      <Skeleton position="absolute" inset={0} rounded="md" opacity={0.32} />
      <Box
        aria-hidden="true"
        position="absolute"
        left={4}
        right={4}
        bottom={2}
        display="grid"
        gridTemplateColumns="repeat(3, minmax(0, 1fr))"
        gap={4}
      >
        <Skeleton h="10px" rounded="full" />
        <Skeleton h="10px" rounded="full" />
        <Skeleton h="10px" rounded="full" />
      </Box>
    </Box>
  );
}

export function MentionPerformanceTimeline({
  points = [],
  headlineMetric = 'interactions',
  isLoading = false,
  isError = false,
  launchedAt = null,
}: MentionPerformanceTimelineProps) {
  const { t, i18n } = useTranslation();

  const chartData = useMemo(() => {
    const firstCapturedAt = points[0] ? new Date(points[0].capturedAt).getTime() : 0;

    return points.map((point) => {
      const capturedAt = new Date(point.capturedAt);
      const elapsedMinutes = Math.max(
        0,
        Math.round((capturedAt.getTime() - firstCapturedAt) / MINUTE_IN_MS)
      );

      return {
        capturedAt: capturedAt.toLocaleDateString(i18n.language, {
          month: 'short',
          day: 'numeric',
        }),
        elapsedAxisMinutes: elapsedMinutes + 1,
        elapsedLabel: formatElapsedMinutes(elapsedMinutes),
        // Single metric-neutral series so the chart wiring stays platform-agnostic.
        value: headlineMetric === 'views' ? (point.views ?? 0) : point.interactions,
      };
    });
  }, [headlineMetric, i18n.language, points]);
  // Map the launch timestamp onto the same elapsed-minutes axis as the points
  // (+1, matching the log-scale offset). Null unless a launch time lands inside
  // the plotted range, so an out-of-range marker is dropped, not clamped.
  const launchAxisValue = useMemo(
    () => resolveLaunchAxisValue(launchedAt, points),
    [launchedAt, points]
  );
  const formatTick = (value: number) => formatElapsedMinutes(value - 1);
  const chart = useChart({
    data: chartData,
    series: [{ name: 'value' as const, color: 'blue.500' }],
  });

  const tooltip = (
    <Chart.Tooltip
      labelFormatter={(_, payload) => {
        const data = payload?.[0]?.payload as
          | { capturedAt?: string; elapsedLabel?: string }
          | undefined;
        if (!data?.capturedAt || !data.elapsedLabel) return undefined;
        return `${data.capturedAt} (${data.elapsedLabel})`;
      }}
    />
  );

  return (
    <Box>
      <Heading size="sm" mb={3} color="gray.900">
        {t('mentions.intelligence.timelineTitle')}
      </Heading>

      {isLoading ? (
        <TimelineChartSkeleton />
      ) : isError ? (
        <MentionEmptyCard
          tone="error"
          title={t('mentions.intelligence.timelineErrorTitle')}
          description={t('mentions.intelligence.timelineError')}
          data-testid="mention-timeline-error"
        />
      ) : points.length < 2 ? (
        <MentionEmptyCard
          title={t('mentions.intelligence.timelineEmptyTitle')}
          description={t('mentions.intelligence.timelineEmpty')}
          data-testid="mention-timeline-empty"
        />
      ) : (
        <Box h="152px" data-testid="mention-timeline-chart">
          <Chart.Root width="full" height="full" chart={chart}>
            <AreaChart data={chart.data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <Chart.Gradient
                  id="mention-timeline-value"
                  stops={[
                    { offset: '0%', color: 'blue.500', opacity: 0.28 },
                    { offset: '100%', color: 'blue.500', opacity: 0.04 },
                  ]}
                />
              </defs>
              <CartesianGrid
                vertical={false}
                stroke="var(--chakra-colors-gray-100)"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey={chart.key('elapsedAxisMinutes')}
                type="number"
                scale="log"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatTick}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'var(--chakra-colors-gray-500)' }}
              />
              <YAxis hide domain={[0, 'dataMax']} />
              <Tooltip content={tooltip} cursor={false} />
              <Area
                type="monotone"
                dataKey={chart.key('value')}
                stroke={chart.color('blue.500')}
                strokeWidth={2}
                fill="url(#mention-timeline-value)"
                isAnimationActive={false}
              />
              {launchAxisValue !== null && (
                // "From here, we boosted": a vertical marker at the boost launch
                // time. Dropped entirely when the launch falls outside the curve.
                <ReferenceLine
                  x={launchAxisValue}
                  stroke="var(--chakra-colors-gray-400)"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  ifOverflow="hidden"
                  label={{
                    value: t('mentions.intelligence.timelineLaunchMarker'),
                    position: 'insideTopRight',
                    fill: 'var(--chakra-colors-gray-500)',
                    fontSize: 10,
                  }}
                  data-testid="mention-timeline-launch-marker"
                />
              )}
            </AreaChart>
          </Chart.Root>
        </Box>
      )}
    </Box>
  );
}
